function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class CurrencySelector extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if(!this.form) {
      throw new Error('No localization form found.');
    }
    if(!this.querySelector('input[name="currency"]')) {
      const input = document.createElement('input');
      input.setAttribute('name', 'currency');
      input.setAttribute('type', 'hidden');
      this.form.append(input)
    }
    const codes = this.querySelectorAll('[data-currency-code]');
    codes.forEach(i => {
      i.addEventListener('click', this.selectCurrency.bind(this));
    })
  }

  get form() {
    return this.querySelector('form');
  }

  get input() {
    return this.querySelector('input[name="currency"]');
  }

  selectCurrency(e) {
    const code = e.target.dataset.currencyCode;
    this.input.value = code;
    this.form.submit();
  }
}

customElements.define('currency-selector', CurrencySelector);

class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }
}

customElements.define('details-modal', DetailsModal);

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function calcHeight(element, container, display = 'block') {
  const clone = element.cloneNode(true);
  Object.assign(clone.style, {
    overflow: 'visible',
    height: 'auto',
    maxHeight: 'none',
    opacity: '0',
    visibility: 'hidden',
    display: display
  });
  container.after(clone);
  const height = clone.offsetHeight;
  clone.remove();
  return height;
}

function horizontalLoop(items, config) {
  gsap.registerPlugin(Draggable)
  let timeline;
  items = gsap.utils.toArray(items);
  config = config || {};
  gsap.context(() => { // use a context so that if this is called from within another context or a gsap.matchMedia(), we can perform proper cleanup like the "resize" event handler on the window
    let onChange = config.onChange,
      lastIndex = 0,
      tl = gsap.timeline({repeat: config.repeat, onUpdate: onChange && function() {
          let i = tl.closestIndex();
          if (lastIndex !== i) {
            lastIndex = i;
            onChange(items[i], i);
          }
        }, paused: config.paused, defaults: {ease: "none"}, onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100)}),
      length = items.length,
      startX = items[0].offsetLeft,
      times = [],
      widths = [],
      spaceBefore = [],
      xPercents = [],
      curIndex = 0,
      indexIsDirty = false,
      center = config.center,
      pixelsPerSecond = (config.speed || 1) * 100,
      snap = config.snap === false ? v => v : gsap.utils.snap(config.snap || 1), // some browsers shift by a pixel to accommodate flex layouts, so for example if width is 20% the first element's width might be 242px, and the next 243px, alternating back and forth. So we snap to 5 percentage points to make things look more natural
      timeOffset = 0,
      container = center === true ? items[0].parentNode : gsap.utils.toArray(center)[0] || items[0].parentNode,
      totalWidth,
      getTotalWidth = () => items[length-1].offsetLeft + xPercents[length-1] / 100 * widths[length-1] - startX + spaceBefore[0] + items[length-1].offsetWidth * gsap.getProperty(items[length-1], "scaleX") + (parseFloat(config.paddingRight) || 0),
      populateWidths = () => {
        let b1 = container.getBoundingClientRect(), b2;
        items.forEach((el, i) => {
          widths[i] = parseFloat(gsap.getProperty(el, "width", "px"));
          xPercents[i] = snap(parseFloat(gsap.getProperty(el, "x", "px")) / widths[i] * 100 + gsap.getProperty(el, "xPercent"));
          b2 = el.getBoundingClientRect();
          spaceBefore[i] = b2.left - (i ? b1.right : b1.left);
          b1 = b2;
        });
        gsap.set(items, { // convert "x" to "xPercent" to make things responsive, and populate the widths/xPercents Arrays to make lookups faster.
          xPercent: i => xPercents[i]
        });
        totalWidth = getTotalWidth();
      },
      timeWrap,
      populateOffsets = () => {
        timeOffset = center ? tl.duration() * (container.offsetWidth / 2) / totalWidth : 0;
        center && times.forEach((t, i) => {
          times[i] = timeWrap(tl.labels["label" + i] + tl.duration() * widths[i] / 2 / totalWidth - timeOffset);
        });
      },
      getClosest = (values, value, wrap) => {
        let i = values.length,
          closest = 1e10,
          index = 0, d;
        while (i--) {
          d = Math.abs(values[i] - value);
          if (d > wrap / 2) {
            d = wrap - d;
          }
          if (d < closest) {
            closest = d;
            index = i;
          }
        }
        return index;
      },
      populateTimeline = () => {
        let i, item, curX, distanceToStart, distanceToLoop;
        tl.clear();
        for (i = 0; i < length; i++) {
          item = items[i];
          curX = xPercents[i] / 100 * widths[i];
          distanceToStart = item.offsetLeft + curX - startX + spaceBefore[0];
          distanceToLoop = distanceToStart + widths[i] * gsap.getProperty(item, "scaleX");
          tl.to(item, {xPercent: snap((curX - distanceToLoop) / widths[i] * 100), duration: distanceToLoop / pixelsPerSecond}, 0)
            .fromTo(item, {xPercent: snap((curX - distanceToLoop + totalWidth) / widths[i] * 100)}, {xPercent: xPercents[i], duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond, immediateRender: false}, distanceToLoop / pixelsPerSecond)
            .add("label" + i, distanceToStart / pixelsPerSecond);
          times[i] = distanceToStart / pixelsPerSecond;
        }
        timeWrap = gsap.utils.wrap(0, tl.duration());
      },
      refresh = (deep) => {
        let progress = tl.progress();
        tl.progress(0, true);
        populateWidths();
        deep && populateTimeline();
        populateOffsets();
        deep && tl.draggable ? tl.time(times[curIndex], true) : tl.progress(progress, true);
      },
      onResize = () => !config.disable_resize && refresh(true),
      proxy;
    gsap.set(items, {x: 0});
    populateWidths();
    populateTimeline();
    populateOffsets();
    window.addEventListener("resize", onResize);
    function toIndex(index, vars) {
      vars = vars || {};
      (Math.abs(index - curIndex) > length / 2) && (index += index > curIndex ? -length : length); // always go in the shortest direction
      let newIndex = gsap.utils.wrap(0, length, index),
        time = times[newIndex];
      if (time > tl.time() !== index > curIndex && index !== curIndex) { // if we're wrapping the timeline's playhead, make the proper adjustments
        time += tl.duration() * (index > curIndex ? 1 : -1);
      }
      if (time < 0 || time > tl.duration()) {
        vars.modifiers = {time: timeWrap};
      }
      curIndex = newIndex;
      vars.overwrite = true;
      gsap.killTweensOf(proxy);    
      return vars.duration === 0 ? tl.time(timeWrap(time)) : tl.tweenTo(time, vars);
    }
    tl.toIndex = (index, vars) => toIndex(index, vars);
    tl.closestIndex = setCurrent => {
      let index = getClosest(times, tl.time(), tl.duration());
      if (setCurrent) {
        curIndex = index;
        indexIsDirty = false;
      }
      return index;
    };
    tl.current = () => indexIsDirty ? tl.closestIndex(true) : curIndex;
    tl.next = vars => toIndex(tl.current()+1, vars);
    tl.previous = vars => toIndex(tl.current()-1, vars);
    tl.times = times;
    tl.progress(1, true).progress(0, true); // pre-render for performance
    if (config.reversed) {
      tl.vars.onReverseComplete();
      tl.reverse();
    }
    if (config.draggable && typeof(Draggable) === "function") {
      proxy = document.createElement("div")
      let wrap = gsap.utils.wrap(0, 1),
        ratio, startProgress, draggable, dragSnap, lastSnap, initChangeX, wasPlaying,
        align = () => tl.progress(wrap(startProgress + (draggable.startX - draggable.x) * ratio)),
        syncIndex = () => tl.closestIndex(true);
      typeof(InertiaPlugin) === "undefined" && console.warn("InertiaPlugin required for momentum-based scrolling and snapping. https://greensock.com/club");
      draggable = Draggable.create(proxy, {
        trigger: items[0].parentNode,
        type: "x",
        onPressInit() {
          let x = this.x;
          gsap.killTweensOf(tl);
          wasPlaying = !tl.paused();
          tl.pause();
          startProgress = tl.progress();
          refresh();
          ratio = 1 / totalWidth;
          initChangeX = (startProgress / -ratio) - x;
          gsap.set(proxy, {x: startProgress / -ratio});
        },
        onDrag: align,
        onThrowUpdate: align,
        overshootTolerance: 0,
        inertia: true,
        snap(value) {
          //note: if the user presses and releases in the middle of a throw, due to the sudden correction of proxy.x in the onPressInit(), the velocity could be very large, throwing off the snap. So sense that condition and adjust for it. We also need to set overshootTolerance to 0 to prevent the inertia from causing it to shoot past and come back
          if (Math.abs(startProgress / -ratio - this.x) < 10) {
            return lastSnap + initChangeX
          }
          let time = -(value * ratio) * tl.duration(),
            wrappedTime = timeWrap(time),
            snapTime = times[getClosest(times, wrappedTime, tl.duration())],
            dif = snapTime - wrappedTime;
          Math.abs(dif) > tl.duration() / 2 && (dif += dif < 0 ? tl.duration() : -tl.duration());
          lastSnap = (time + dif) / tl.duration() / -ratio;
          return lastSnap;
        },
        onRelease() {
          syncIndex();
          draggable.isThrowing && (indexIsDirty = true);
        },
        onThrowComplete: () => {
          syncIndex();
          wasPlaying && tl.play();
        }
      })[0];
      tl.draggable = draggable;
    }
    tl.closestIndex(true);
    lastIndex = curIndex;
    onChange && onChange(items[curIndex], curIndex);
    timeline = tl;
    return () => window.removeEventListener("resize", onResize); // cleanup
  });
  return timeline;
}

function getSectionInnerHTML(html, selector) {
  return new DOMParser()
    .parseFromString(html, 'text/html')
    .querySelector(selector).innerHTML;
}

function pauseAllMedia(element = null) {
  if(element == null) {
    element = document;
  }
  element.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  element.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  element.querySelectorAll('video').forEach((video) => video.pause());
  element.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

class DeferredMedia extends HTMLElement {
  connectedCallback() {
    this.shouldAutoPlay = this.dataset.shouldAutoplay;
    if(this.shouldAutoPlay) {
      window.setTimeout(() => {
        this.loadContent(false);
      }, 700)
    }
    this.carousel = this.closest('carousel-block');
    if(this.carousel) {
      this.carousel.addEventListener('slide', this.toggleAutoPlay.bind(this))
    }
    this.poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (this.poster) {
      this.poster.addEventListener('click', this.loadContent.bind(this));
    }
  }

  toggleAutoPlay() {
    if(!this.deferredElement || this.deferredElement.nodeName != 'VIDEO') {
      return;
    }
    if(this.carousel.isVisible(this) && this.deferredElement.getAttribute('autoplay')) {
      this.deferredElement?.play();
    }
    else {
      this.deferredElement?.pause();
    }
  }

  loadContent(focus = true) {
    pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));
      this.setAttribute('loaded', true);
      this.poster.querySelector('img').classList.add('blur-md');
      this.deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) this.deferredElement.focus();
      if (this.deferredElement.nodeName == 'VIDEO' && this.deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        this.deferredElement.muted = true;
        this.deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

if (!customElements.get('share-button')) {
  customElements.define(
    'share-button',
    class ShareButton extends DetailsDisclosure {
      constructor() {
        super();

        this.elements = {
          shareButton: this.querySelector('button'),
          shareSummary: this.querySelector('summary'),
          closeButton: this.querySelector('.share-button__close'),
          successMessage: this.querySelector('[id^="ShareMessage"]'),
          urlInput: this.querySelector('input'),
        };
        this.urlToShare = this.elements.urlInput ? this.elements.urlInput.value : document.location.href;

        if (navigator.share) {
          this.mainDetailsToggle.setAttribute('hidden', '');
          this.elements.shareButton.classList.remove('hidden');
          this.elements.shareButton.addEventListener('click', () => {
            navigator.share({ url: this.urlToShare, title: document.title });
          });
        } else {
          this.mainDetailsToggle.addEventListener('toggle', this.toggleDetails.bind(this));
          this.mainDetailsToggle
            .querySelector('.share-button__copy')
            .addEventListener('click', this.copyToClipboard.bind(this));
          this.mainDetailsToggle.querySelector('.share-button__close').addEventListener('click', this.close.bind(this));
        }
      }

      toggleDetails() {
        if (!this.mainDetailsToggle.open) {
          this.elements.successMessage.classList.add('hidden');
          this.elements.successMessage.textContent = '';
          this.elements.closeButton.classList.add('hidden');
          this.elements.shareSummary.focus();
        }
      }

      copyToClipboard() {
        navigator.clipboard.writeText(this.elements.urlInput.value).then(() => {
          this.elements.successMessage.classList.remove('hidden');
          this.elements.successMessage.textContent = 'Link copied to clipboard';
          this.elements.closeButton.classList.remove('hidden');
          this.elements.closeButton.focus();
        });
      }

      updateUrl(url) {
        this.urlToShare = url;
        this.elements.urlInput.value = url;
      }
    }
  );
}

const waitForImageToLoad = (imageElement) => {
  return new Promise(resolve=> {
    const src = imageElement.src;
    imageElement.onload = resolve
    imageElement.src = src;
  })
}

const resizeHeadings = () => {
  const headings = document.querySelectorAll('.heading-block');
  headings.forEach(i => {
    i.classList.remove('shrink-required');
    const headingWidth = i.parentElement.scrollWidth;
    const paddingMobile = 48;
    const pageWidth = window.innerWidth - paddingMobile;
    const shrinkFactor = pageWidth / headingWidth;
    if(shrinkFactor < 1) {
      const textSize = parseFloat(window.getComputedStyle(i).getPropertyValue('font-size'));
      i.style.setProperty("--shrink-text", `${Math.floor(textSize * shrinkFactor)}px`);
      i.classList.add('shrink-required')
    }
  })
}

const setupPayNowButtons = () => {
  const wrappers = document.querySelectorAll('.payment-button-wrapper');
  wrappers.forEach(i => {
    const template = i.querySelector('.button-template');
    if(!template) {
      return;
    }
    const styles = window.getComputedStyle(template);
    i.querySelectorAll('button').forEach(b => {
      b.style.borderWidth = styles.borderWidth;
      b.style.borderStyle = styles.borderStyle;
      b.style.padding = styles.padding;
      b.style.borderRadius = styles.borderRadius;
      b.style.width = styles.width === '100%' ? '100%' : 'auto';
    });
    i.style.opacity = 100;
  })
}

window.onload = () => {
  resizeHeadings();
  window.addEventListener('resize', debounce(() => resizeHeadings(), 100));
  setupPayNowButtons();
}

gsap.registerPlugin(ScrollSmoother, ScrollTrigger);

const createSmoothScroll = () => {
  ScrollSmoother.create({
    smooth: 2,
    effects: true,
    normalizeScroll: true,
    ignoreMobileResize: true,
    wholePixels: true
  });
}

const animationSetup = () => {
  createSmoothScroll();
}

const toTop = () => {
  gsap.to(window, {
    duration: 0.5, 
    scrollTo: 0
  })
}

document.addEventListener("DOMContentLoaded", animationSetup);

class FeaturedCollection extends HTMLElement {
  connectedCallback() {
    if(this.dataset.enableToggles !== 'true') {
      return;
    }
    this.id = this.dataset.sectionId;
    this.normalView = this.querySelector('[data-normal-view]');
    this.listView = this.querySelector('[data-list-view]');
    this.showMoreButton = this.querySelector('[data-calendar-more]');
    this.normalToggle = this.querySelectorAll(`[data-toggle-normal="${this.id}"]`);
    this.listToggle = this.querySelectorAll(`[data-toggle-list="${this.id}"]`);
    this.calendarFilter = this.querySelector('[data-calendar-filter]');
    this.normalToggle.forEach(i => {
      i.addEventListener('click', () => this.setView('normal'));
    });
    this.listToggle.forEach(i => {
      i.addEventListener('click', () => this.setView('list'));
    });
    this.calendarRowView = 1;
    if(this.showMoreButton) {
      this.showMoreButton.addEventListener('click', this.handleShowMore.bind(this));
    }
    if(this.calendarFilter) {
      this.calendarFilter.querySelectorAll('li').forEach(i => {
        i.addEventListener('click', this.handleSelectFilter.bind(this, i));
      })
    }
    window.addEventListener('resize', this.handleResize.bind(this));
    this.setView(this.dataset.startState === 'list' ? 'list' : 'normal', { initial: true });
  }

  get activeView() {
    return this.currentView === 'list' ? this.listView : this.normalView;
  }

  setView(view, { initial = false } = {}) {
    if(!initial && this.currentView === view) {
      return;
    }
    this.currentView = view;
    this.normalView.style.display = view === 'normal' ? 'grid' : 'none';
    this.listView.style.display = view === 'list' ? 'flex' : 'none';
    this.normalToggle.forEach(i => {
      i.setAttribute('data-state', view === 'normal' ? 'active' : 'inactive');
    });
    this.listToggle.forEach(i => {
      i.setAttribute('data-state', view === 'list' ? 'active' : 'inactive');
    });
    const targetScheme = view === 'list' ? this.dataset.listColorScheme : this.dataset.colorScheme;
    const currentScheme = this.dataset.appliedColorScheme || this.dataset.colorScheme;
    if(targetScheme && currentScheme !== targetScheme) {
      if(currentScheme) {
        this.classList.remove(`color-${currentScheme}`);
      }
      this.classList.add(`color-${targetScheme}`);
      this.dataset.appliedColorScheme = targetScheme;
    }
    this.calendarRowView = 1;
    if(this.calendarFilter) {
      this.calendarFilter.style.display = 'block';
      const activeFilter = this.calendarFilter.querySelector('li[data-state="active"]') ?? this.calendarFilter.querySelector('li');
      this.selectFilter(activeFilter);
    }
    else {
      this.calendarCards = Array.from(this.activeView.children);
      this.limitCalendarColumnView();
    }
  }

  get filter() {
    return this.calendarFilter.querySelector('li[data-state="active"]')?.dataset.value ?? 'upcoming';
  }

  handleSelectFilter(el, e) {
    e.preventDefault();
    this.selectFilter(el);
  }

  selectFilter(el) {
    this.calendarFilter.querySelectorAll('li[data-state="active"]').forEach(i => {
      i.setAttribute('data-state', 'inactive');
    });
    el.setAttribute('data-state', 'active');
    this.calendarFilter.querySelector('.filter-value').innerHTML = el.innerHTML;
    if (typeof this.calendarFilter.close === 'function') {
      this.calendarFilter.close();
    }
    this.filterCalendarCards();
    this.calendarRowView = 1;
    this.limitCalendarColumnView();
  }

  getGridColumnCount() {
    if(this.currentView === 'list') {
      return 6;
    }
    const value = getComputedStyle(this.normalView).gridTemplateColumns;
    const repeatMatch = value.match(/^repeat\((\d+),/);
    if (repeatMatch) return parseInt(repeatMatch[1]);
    return value.trim().split(/\s+(?![^(]*\))/).filter(Boolean).length;
  }

  handleResize(e) {
    e.preventDefault();
    this.resizeCalendar();
  }

  resizeCalendar() {
    const resizedMaxView = this.calendarRowView * this.getGridColumnCount();
    if(this.calendarMaxView != resizedMaxView) {
      this.limitCalendarColumnView();
    }
  }

  getCardStatus(el) {
    return el.querySelector('product-card')?.dataset.status;
  }

  getCardTimestamp(el) {
    const value = parseInt(el.querySelector('product-card')?.dataset.eventTimestamp);
    return Number.isNaN(value) ? null : value;
  }

  filterCalendarCards() {
    const filter = this.filter;
    const container = this.activeView;
    let cards = Array.from(container.children);
    cards.forEach(i => { i.style.display = 'none'; });

    if(filter === 'available') {
      cards = cards.filter(i => this.getCardStatus(i) === 'open');
    }
    else if(filter === 'past') {
      cards = cards.filter(i => ['past', 'sold-out'].includes(this.getCardStatus(i)));
    }

    if(filter === 'upcoming') {
      cards.sort((a, b) => {
        const aPast = ['past', 'sold-out'].includes(this.getCardStatus(a));
        const bPast = ['past', 'sold-out'].includes(this.getCardStatus(b));
        if(aPast !== bPast) return aPast ? 1 : -1;
        const ta = this.getCardTimestamp(a);
        const tb = this.getCardTimestamp(b);
        if(aPast) {
          // Past/sold-out group: most recently passed first, undated last.
          return (tb ?? -Infinity) - (ta ?? -Infinity);
        }
        // Upcoming group: soonest first, undated last.
        return (ta ?? Infinity) - (tb ?? Infinity);
      });
      cards.forEach(el => container.appendChild(el));
    }
    else if(filter === 'available') {
      cards.sort((a, b) => (this.getCardTimestamp(a) ?? Infinity) - (this.getCardTimestamp(b) ?? Infinity));
      cards.forEach(el => container.appendChild(el));
    }
    else if(filter === 'past') {
      cards.sort((a, b) => (this.getCardTimestamp(b) ?? -Infinity) - (this.getCardTimestamp(a) ?? -Infinity));
      cards.forEach(el => container.appendChild(el));
    }

    this.calendarCards = cards;
  }

  limitCalendarColumnView() {
    const visibleDisplay = this.currentView === 'list' ? 'flex' : 'block';
    this.calendarMaxView = this.calendarRowView * this.getGridColumnCount();
    this.calendarCards.forEach((i,k) => {
      i.style.display = k >= this.calendarMaxView ? 'none' : visibleDisplay;
    });
    this.toggleShowMoreButton(this.calendarCards.length > this.calendarMaxView);
  }

  toggleShowMoreButton(show = true) {
    if(show) {
      this.showMoreButton.style.display = 'flex';
    }
    else {
      this.showMoreButton.style.display = 'none';
    }
  }

  handleShowMore(e) {
    e.preventDefault();
    this.showMoreCalendarColumns();
  }

  showMoreCalendarColumns() {
    const columnCount = this.getGridColumnCount();
    this.calendarRowView = this.calendarRowView + (columnCount > 1 ? 1 : 3);
    this.limitCalendarColumnView();
  }
}

customElements.define('featured-collection', FeaturedCollection);

class LanguageSelector extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if(!this.form) {
      throw new Error('No localization form found.');
    }
    if(!this.querySelector('input[name="language_code"]')) {
      const input = document.createElement('input');
      input.setAttribute('name', 'language_code');
      input.setAttribute('type', 'hidden');
      this.form.append(input)
    }
    const codes = this.querySelectorAll('[data-language-code]');
    codes.forEach(i => {
      i.addEventListener('click', this.selectLanguage.bind(this));
    })
  }

  get form() {
    return this.querySelector('form');
  }

  get input() {
    return this.querySelector('input[name="language_code"]');
  }

  selectLanguage(e) {
    e.preventDefault();
    const code = e.currentTarget.dataset.languageCode;
    this.input.value = code;
    this.form.submit();
  }
}

customElements.define('language-selector', LanguageSelector);

class FormField extends HTMLElement {
  connectedCallback() {
    this.hasPlaceholder = this.dataset.placeholder.length > 0;
    const fields = this.querySelectorAll('input, select, textarea');
    if(fields.length > 0) {
      this.field = fields[0];
      this.field.addEventListener('change', this.handleChange.bind(this));
      this.setFieldState();
    }
  }

  handleChange(e) {
    e.preventDefault();
    this.setFieldState();
  }
  
  setFieldState() {
    this.setAttribute('data-state', (!this.field.value.length || (this.field.tagName === 'SELECT' && this.field.value === 'null')) ? (this.hasPlaceholder ? 'filled' : 'empty') : 'filled');
  }
}

customElements.define('form-field', FormField);